import React, { useState, useEffect } from 'react';
import { Nav, Navbar, Collapse, Badge } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTachometerAlt, faUsers, faShoppingCart, faIndustry, faMoneyBillWave,
  faChartLine, faTools, faFileAlt, faCog, faUserTie, faWarehouse,
  faFlask, faBullhorn, faHeadset, faStore, faUsersCog, faBuilding,
  faChevronDown, faChevronRight, faBell, faSignOutAlt, faUser,
  faBox, faClipboardList, faTasks, faCalendarCheck, faMapMarkerAlt,
  faCheckCircle, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import './Sidebar.css';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State untuk collapse menu
  const [expandedMenus, setExpandedMenus] = useState({});
  const [userRole, setUserRole] = useState('');
  const [username, setUsername] = useState('');
  const [userInfo, setUserInfo] = useState({});

  // Get user info dari localStorage dengan fallback
  useEffect(() => {
    const getUserInfo = () => {
      // Ambil semua data user dari localStorage
      const userData = {
        username: localStorage.getItem('username') || 'unknown',
        role: localStorage.getItem('role') || localStorage.getItem('userRole') || 'user',
        firstName: localStorage.getItem('firstName') || '',
        lastName: localStorage.getItem('lastName') || '',
        email: localStorage.getItem('email') || '',
        userId: localStorage.getItem('userId') || localStorage.getItem('user_id'),
        isStaff: localStorage.getItem('isStaff') === 'true',
        isSuperuser: localStorage.getItem('isSuperuser') === 'true'
      };

      console.log('[Sidebar] User data from localStorage:', userData);

      setUsername(userData.username);
      setUserRole(userData.role);
      setUserInfo(userData);

      // Auto-expand menu berdasarkan current path
      const currentPath = location.pathname;
      if (currentPath.includes('/dashboard/marketing')) {
        setExpandedMenus(prev => ({ ...prev, marketing: true }));
      } else if (currentPath.includes('/hrd')) {
        setExpandedMenus(prev => ({ ...prev, hrd: true }));
      } else if (currentPath.includes('/produksi')) {
        setExpandedMenus(prev => ({ ...prev, produksi: true }));
      } else if (currentPath.includes('/gudang')) {
        setExpandedMenus(prev => ({ ...prev, gudang: true }));
      } else if (currentPath.includes('/rnd')) {
        setExpandedMenus(prev => ({ ...prev, rnd: true }));
      } else if (currentPath.includes('/tools')) {
        setExpandedMenus(prev => ({ ...prev, tools: true }));
      } else if (currentPath.includes('/laporan')) {
        setExpandedMenus(prev => ({ ...prev, laporan: true }));
      }
    };

    getUserInfo();
  }, [location.pathname]);

  // Toggle menu expansion
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Check if user has permission untuk menu tertentu
  const hasPermission = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    
    const currentRole = userRole.toLowerCase();
    const currentUsername = username.toLowerCase();
    
    // Owner dan admin punya akses ke semua
    if (currentUsername === 'mbotee' || currentRole.includes('owner') || 
        currentRole.includes('admin') || userInfo.isSuperuser) {
      return true;
    }
    
    // Check specific roles
    return requiredRoles.some(role => {
      const roleToCheck = role.toLowerCase();
      return currentRole.includes(roleToCheck) || 
             currentUsername.includes(roleToCheck) ||
             (roleToCheck === 'marketing' && (
               currentRole.includes('cs') || 
               currentRole.includes('retail') || 
               currentRole.includes('marketing')
             ));
    });
  };

  // Get dashboard URL berdasarkan role
  const getDashboardUrl = () => {
    const role = userRole.toLowerCase();
    const user = username.toLowerCase();
    
    // Special users
    if (user === 'mbotee' || role.includes('owner')) {
      return '/dashboard';
    }
    
    // Role-based dashboards
    if (role.includes('cs') || role.includes('customer_service')) {
      return '/dashboard/marketing/cs';
    }
    if (role.includes('retail') || role.includes('retail_rep')) {
      return '/dashboard/marketing/retail';
    }
    if (role.includes('marketing') || role.includes('spv_marketing')) {
      return '/dashboard/marketing';
    }
    if (role.includes('produksi') || role.includes('production')) {
      return '/produksi/dashboard';
    }
    if (role.includes('gudang') || role.includes('warehouse')) {
      return '/dashboard/gudang';
    }
    if (role.includes('keuangan') || role.includes('finance')) {
      return '/dashboard/keuangan';
    }
    if (role.includes('hrd') || role.includes('hr')) {
      return '/dashboard/hrd';
    }
    if (role.includes('rnd') || role.includes('research')) {
      return '/dashboard/rnd';
    }
    
    return '/dashboard';
  };

  // Render menu item
  const renderMenuItem = (item) => {
    const isActive = location.pathname === item.path || 
                    (item.activePaths && item.activePaths.some(path => location.pathname.includes(path)));
    
    return (
      <Nav.Item key={item.path} className={`sidebar-nav-item ${isActive ? 'active' : ''}`}>
        <Nav.Link as={Link} to={item.path} className="sidebar-nav-link">
          <FontAwesomeIcon icon={item.icon} className="sidebar-icon" />
          <span className="sidebar-text">{item.title}</span>
          {item.badge && (
            <Badge bg={item.badge.variant} className="ms-auto">
              {item.badge.text}
            </Badge>
          )}
        </Nav.Link>
      </Nav.Item>
    );
  };

  // Render collapsible menu
  const renderCollapsibleMenu = (menuKey, title, icon, items, requiredRoles = []) => {
    if (!hasPermission(requiredRoles)) return null;
    
    const isExpanded = expandedMenus[menuKey];
    const hasActiveChild = items.some(item => 
      location.pathname === item.path || 
      (item.activePaths && item.activePaths.some(path => location.pathname.includes(path)))
    );

    return (
      <div key={menuKey} className="sidebar-menu-group">
        <Nav.Item 
          className={`sidebar-nav-item ${hasActiveChild ? 'has-active-child' : ''}`}
          onClick={() => toggleMenu(menuKey)}
          style={{ cursor: 'pointer' }}
        >
          <Nav.Link className="sidebar-nav-link">
            <FontAwesomeIcon icon={icon} className="sidebar-icon" />
            <span className="sidebar-text">{title}</span>
            <FontAwesomeIcon 
              icon={isExpanded ? faChevronDown : faChevronRight} 
              className="sidebar-chevron ms-auto" 
            />
          </Nav.Link>
        </Nav.Item>
        
        <Collapse in={isExpanded}>
          <div className="sidebar-submenu">
            {items.map(item => {
              if (item.requiredRoles && !hasPermission(item.requiredRoles)) {
                return null;
              }
              return renderMenuItem({
                ...item,
                path: item.path,
                title: item.title,
                icon: item.icon
              });
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  // Menu configuration
  const menuConfig = {
    // Dashboard utama
    dashboard: {
      title: 'Dashboard',
      icon: faTachometerAlt,
      path: getDashboardUrl(),
      requiredRoles: []
    },

    // Marketing Dashboard (dengan submenu yang diperbaiki)
    marketing: {
      title: 'Marketing',
      icon: faBullhorn,
      requiredRoles: ['marketing', 'cs', 'retail', 'spv_marketing', 'owner', 'admin'],
      items: [
        {
          title: 'Marketing Overview',
          icon: faChartLine,
          path: '/dashboard/marketing',
          requiredRoles: ['marketing', 'spv_marketing', 'owner', 'admin'],
          activePaths: ['/dashboard/marketing']
        },
        {
          title: 'Marketing Plans',
          icon: faCalendarAlt,
          path: '/dashboard/marketing/plans',
          requiredRoles: ['marketing', 'spv_marketing', 'owner', 'admin'],
          activePaths: ['/dashboard/marketing/plans']
        },
        {
          title: 'Marketing Campaigns',
          icon: faBullhorn,
          path: '/marketing-campaigns',
          requiredRoles: ['marketing', 'spv_marketing', 'owner', 'admin'],
          activePaths: ['/marketing-campaigns']
        },
        {
          title: 'CS Dashboard',
          icon: faHeadset,
          path: '/dashboard/marketing/cs',
          requiredRoles: ['cs', 'customer_service', 'marketing', 'owner', 'admin'],
          activePaths: ['/dashboard/marketing/cs']
        },
        {
          title: 'Retail Dashboard',
          icon: faStore,
          path: '/dashboard/marketing/retail',
          requiredRoles: ['retail', 'retail_rep', 'marketing', 'owner', 'admin'],
          activePaths: ['/dashboard/marketing/retail']
        },
        {
          title: 'SPV Marketing',
          icon: faUserTie,
          path: '/dashboard/marketing/spv',
          requiredRoles: ['spv_marketing', 'marketing_supervisor', 'owner', 'admin']
        }
      ]
    },

    // Order Management
    orders: {
      title: 'Orders',
      icon: faShoppingCart,
      requiredRoles: ['marketing', 'cs', 'retail', 'admin', 'owner'],
      items: [
        {
          title: 'Daftar Order',
          icon: faClipboardList,
          path: '/order-list',
          requiredRoles: []
        },
        {
          title: 'Input Order Baru',
          icon: faShoppingCart,
          path: '/form-input-order',
          requiredRoles: ['marketing', 'cs', 'retail', 'admin', 'owner']
        }
      ]
    },

    // Customer Management
    customers: {
      title: 'Customers',
      icon: faUsers,
      requiredRoles: ['marketing', 'cs', 'retail', 'admin', 'owner'],
      items: [
        {
          title: 'Daftar Customer',
          icon: faUsers,
          path: '/customers',
          requiredRoles: []
        },
        {
          title: 'Tambah Customer',
          icon: faUser,
          path: '/customers/add',
          requiredRoles: ['marketing', 'cs', 'retail', 'admin', 'owner']
        }
      ]
    },

    // Production
    produksi: {
      title: 'Produksi',
      icon: faIndustry,
      requiredRoles: ['produksi', 'production', 'admin', 'owner'],
      items: [
        {
          title: 'Dashboard Produksi',
          icon: faTachometerAlt,
          path: '/produksi/dashboard',
          requiredRoles: []
        },
        {
          title: 'Daftar Order',
          icon: faClipboardList,
          path: '/produksi/daftar-order',
          requiredRoles: []
        },
        {
          title: 'My Tasks',
          icon: faTasks,
          path: '/produksi/my-tasks',
          requiredRoles: []
        },
        {
          title: 'Available Tasks',
          icon: faCalendarCheck,
          path: '/produksi/available-tasks',
          requiredRoles: []
        }
      ]
    },

    // Warehouse/Gudang
    gudang: {
      title: 'Gudang',
      icon: faWarehouse,
      requiredRoles: ['gudang', 'warehouse', 'admin', 'owner'],
      items: [
        {
          title: 'Dashboard Gudang',
          icon: faTachometerAlt,
          path: '/dashboard/gudang',
          requiredRoles: []
        },
        {
          title: 'Inventaris',
          icon: faBox,
          path: '/tools/inventaris-gudang',
          requiredRoles: []
        },
        {
          title: 'Stock Bahan',
          icon: faBox,
          path: '/gudang/bahan-baku',
          requiredRoles: []
        },
        {
          title: 'Produk Jadi',
          icon: faBox,
          path: '/gudang/produk-jadi',
          requiredRoles: []
        }
      ]
    },

    // Finance/Keuangan
    keuangan: {
      title: 'Keuangan',
      icon: faMoneyBillWave,
      requiredRoles: ['keuangan', 'finance', 'admin', 'owner'],
      items: [
        {
          title: 'Dashboard Keuangan',
          icon: faTachometerAlt,
          path: '/dashboard/keuangan',
          requiredRoles: []
        },
        {
          title: 'Daftar Pembayaran',
          icon: faMoneyBillWave,
          path: '/keuangan/daftar-pembayaran',
          requiredRoles: []
        },
        {
          title: 'Admin Keuangan',
          icon: faUserTie,
          path: '/dashboard/admin-keuangan',
          requiredRoles: ['admin', 'owner']
        }
      ]
    },

    // HRD
    hrd: {
      title: 'HRD',
      icon: faUsersCog,
      requiredRoles: ['hrd', 'hr', 'admin', 'owner'],
      items: [
        {
          title: 'Dashboard HRD',
          icon: faTachometerAlt,
          path: '/dashboard/hrd',
          requiredRoles: []
        },
        {
          title: 'User Management',
          icon: faUsers,
          path: '/hrd/user-management',
          requiredRoles: []
        },
        {
          title: 'Role Management',
          icon: faUsersCog,
          path: '/hrd/role-management',
          requiredRoles: []
        },
        {
          title: 'Department',
          icon: faBuilding,
          path: '/hrd/departments',
          requiredRoles: []
        }
      ]
    },

    // R&D
    rnd: {
      title: 'Research & Development',
      icon: faFlask,
      requiredRoles: ['rnd', 'research', 'admin', 'owner'],
      items: [
        {
          title: 'Dashboard R&D',
          icon: faTachometerAlt,
          path: '/dashboard/rnd',
          requiredRoles: []
        },
        {
          title: 'Projects',
          icon: faClipboardList,
          path: '/rnd/projects',
          requiredRoles: []
        },
        {
          title: 'Finished Projects',
          icon: faCheckCircle,
          path: '/rnd/finished-projects',
          requiredRoles: []
        }
      ]
    },

    // Tools
    tools: {
      title: 'Tools',
      icon: faTools,
      requiredRoles: [],
      items: [
        {
          title: 'Plan Kunjungan',
          icon: faMapMarkerAlt,
          path: '/tools/form-plan-kunjungan',
          requiredRoles: ['marketing', 'cs', 'retail', 'admin', 'owner']
        },
        {
          title: 'Realisasi Kunjungan',
          icon: faCalendarCheck,
          path: '/tools/form-realisasi-kunjungan-rr',
          requiredRoles: ['marketing', 'cs', 'retail', 'admin', 'owner']
        },
        {
          title: 'Invoice & Label Auto',
          icon: faFileAlt,
          path: '/tools/invoice-label-auto',
          requiredRoles: ['admin', 'owner']
        },
        {
          title: 'Export Laporan',
          icon: faFileAlt,
          path: '/tools/export-laporan',
          requiredRoles: ['admin', 'owner']
        }
      ]
    },

    // Laporan
    laporan: {
      title: 'Laporan',
      icon: faFileAlt,
      requiredRoles: ['admin', 'owner', 'marketing', 'keuangan'],
      items: [
        {
          title: 'Order Harian',
          icon: faChartLine,
          path: '/laporan/order/harian',
          requiredRoles: []
        },
        {
          title: 'Order Bulanan',
          icon: faChartLine,
          path: '/laporan/order/bulanan',
          requiredRoles: []
        },
        {
          title: 'Grafik Omzet',
          icon: faChartLine,
          path: '/laporan/order/grafik',
          requiredRoles: []
        }
      ]
    }
  };

  // Logout function
  const handleLogout = () => {
    // Clear all localStorage
    localStorage.clear();
    
    // Redirect to login
    navigate('/login');
  };

  return (
    <div className="sidebar-wrapper">
      <Navbar className="sidebar-brand" variant="dark">
        <Navbar.Brand as={Link} to={getDashboardUrl()}>
          <span className="sidebar-brand-text">Rumah Akrilik</span>
        </Navbar.Brand>
      </Navbar>

      <div className="sidebar-content">
        <Nav className="flex-column sidebar-nav">
          {/* Dashboard utama */}
          {hasPermission(menuConfig.dashboard.requiredRoles) && 
            renderMenuItem(menuConfig.dashboard)
          }

          {/* Divider */}
          <hr className="sidebar-divider" />

          {/* Marketing Section */}
          {renderCollapsibleMenu(
            'marketing', 
            menuConfig.marketing.title, 
            menuConfig.marketing.icon, 
            menuConfig.marketing.items, 
            menuConfig.marketing.requiredRoles
          )}

          {/* Orders */}
          {renderCollapsibleMenu(
            'orders', 
            menuConfig.orders.title, 
            menuConfig.orders.icon, 
            menuConfig.orders.items, 
            menuConfig.orders.requiredRoles
          )}

          {/* Customers */}
          {renderCollapsibleMenu(
            'customers', 
            menuConfig.customers.title, 
            menuConfig.customers.icon, 
            menuConfig.customers.items, 
            menuConfig.customers.requiredRoles
          )}

          {/* Divider */}
          <hr className="sidebar-divider" />

          {/* Production */}
          {renderCollapsibleMenu(
            'produksi', 
            menuConfig.produksi.title, 
            menuConfig.produksi.icon, 
            menuConfig.produksi.items, 
            menuConfig.produksi.requiredRoles
          )}

          {/* Warehouse */}
          {renderCollapsibleMenu(
            'gudang', 
            menuConfig.gudang.title, 
            menuConfig.gudang.icon, 
            menuConfig.gudang.items, 
            menuConfig.gudang.requiredRoles
          )}

          {/* Finance */}
          {renderCollapsibleMenu(
            'keuangan', 
            menuConfig.keuangan.title, 
            menuConfig.keuangan.icon, 
            menuConfig.keuangan.items, 
            menuConfig.keuangan.requiredRoles
          )}

          {/* Divider */}
          <hr className="sidebar-divider" />

          {/* HRD */}
          {renderCollapsibleMenu(
            'hrd', 
            menuConfig.hrd.title, 
            menuConfig.hrd.icon, 
            menuConfig.hrd.items, 
            menuConfig.hrd.requiredRoles
          )}

          {/* R&D */}
          {renderCollapsibleMenu(
            'rnd', 
            menuConfig.rnd.title, 
            menuConfig.rnd.icon, 
            menuConfig.rnd.items, 
            menuConfig.rnd.requiredRoles
          )}

          {/* Divider */}
          <hr className="sidebar-divider" />

          {/* Tools */}
          {renderCollapsibleMenu(
            'tools', 
            menuConfig.tools.title, 
            menuConfig.tools.icon, 
            menuConfig.tools.items, 
            menuConfig.tools.requiredRoles
          )}

          {/* Laporan */}
          {renderCollapsibleMenu(
            'laporan', 
            menuConfig.laporan.title, 
            menuConfig.laporan.icon, 
            menuConfig.laporan.items, 
            menuConfig.laporan.requiredRoles
          )}

          {/* Divider */}
          <hr className="sidebar-divider" />
        </Nav>
      </div>

      {/* User info dan logout */}
      <div className="sidebar-footer">
        <div className="sidebar-user-info">
          <div className="user-avatar">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <div className="user-details">
            <div className="user-name">
              {userInfo.firstName || username}
            </div>
            <div className="user-role">
              {userRole}
            </div>
          </div>
        </div>
        
        <Nav.Item className="sidebar-nav-item">
          <Nav.Link as={Link} to="/profile" className="sidebar-nav-link">
            <FontAwesomeIcon icon={faUser} className="sidebar-icon" />
            <span className="sidebar-text">Profile</span>
          </Nav.Link>
        </Nav.Item>
        
        <Nav.Item className="sidebar-nav-item">
          <Nav.Link 
            className="sidebar-nav-link text-danger"
            onClick={handleLogout}
            style={{ cursor: 'pointer' }}
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="sidebar-icon" />
            <span className="sidebar-text">Logout</span>
          </Nav.Link>
        </Nav.Item>
      </div>
    </div>
  );
};

export default Sidebar;